// Portrait — illuminated calligraphic nameplate in a mihrab niche.
// Replaces the old cartoon-silhouette placeholder.
//
// Props:
//   size       height base in px (final height is size * 1.3)
//   initial    single Arabic grapheme — overrides auto-derive
//   nameArabic full Arabic name, first grapheme used as initial if no override
//   tone       'gold' | 'jade' | 'rose' | 'ink'

export function Portrait({ size = 96, initial, nameArabic, tone = 'gold' }) {
  const glyph = initial || (nameArabic ? [...nameArabic][0] : 'ع');

  const TONES = {
    gold: {
      warm: '#2a1f12', hi: 'rgba(255,179,0,0.28)', rim: 'rgba(255,220,160,0.25)',
      rim2: 'rgba(255,220,160,0.12)', glow: 'rgba(255,179,0,0.35)', ink: '#f2c86b',
      finial: ['#ffe090', '#ffb300', '#9a5f00'], finialGlow: '#ffb300',
    },
    jade: {
      warm: '#0f241d', hi: 'rgba(120,220,180,0.22)', rim: 'rgba(180,240,215,0.22)',
      rim2: 'rgba(180,240,215,0.10)', glow: 'rgba(120,220,180,0.30)', ink: '#9fe3c4',
      finial: ['#d8f6e6', '#5fc79a', '#1f5a44'], finialGlow: '#5fc79a',
    },
    rose: {
      warm: '#2a1418', hi: 'rgba(255,150,170,0.22)', rim: 'rgba(255,200,210,0.22)',
      rim2: 'rgba(255,200,210,0.10)', glow: 'rgba(255,150,170,0.30)', ink: '#f0a6b4',
      finial: ['#ffd8e0', '#e06a80', '#6a1e2c'], finialGlow: '#e06a80',
    },
    ink: {
      warm: '#111821', hi: 'rgba(160,190,230,0.22)', rim: 'rgba(200,220,245,0.22)',
      rim2: 'rgba(200,220,245,0.10)', glow: 'rgba(160,190,230,0.28)', ink: '#b7cef0',
      finial: ['#dde8fa', '#6c8bc2', '#1f3352'], finialGlow: '#6c8bc2',
    },
  };

  const c = TONES[tone] || TONES.gold;
  const archTL = size * 0.47;
  const archBL = size * 0.10;

  return (
    <div style={{
      width: size,
      height: size * 1.3,
      borderRadius: `${archTL}px ${archTL}px ${archBL}px ${archBL}px`,
      overflow: 'hidden',
      position: 'relative',
      flexShrink: 0,
      background: `
        radial-gradient(ellipse at 50% 30%, ${c.hi} 0%, rgba(0,0,0,0) 65%),
        linear-gradient(180deg, ${c.warm} 0%, #0d0a07 100%)
      `,
      boxShadow: `
        inset 0 0 0 1px ${c.rim},
        inset 0 -20px 30px rgba(0,0,0,0.5),
        0 16px 36px rgba(0,0,0,0.55)
      `,
    }}>
      {/* inner arch 1 */}
      <div style={{
        position: 'absolute', inset: 5,
        borderRadius: `${size * 0.44}px ${size * 0.44}px ${size * 0.08}px ${size * 0.08}px`,
        border: `0.5px solid ${c.rim}`,
      }} />
      {/* inner arch 2 */}
      <div style={{
        position: 'absolute', inset: 10,
        borderRadius: `${size * 0.42}px ${size * 0.42}px ${size * 0.06}px ${size * 0.06}px`,
        border: `0.5px solid ${c.rim2}`,
      }} />
      {/* lantern glow */}
      <div style={{
        position: 'absolute', top: '22%', left: '50%', transform: 'translateX(-50%)',
        width: size * 0.7, height: size * 0.7, borderRadius: '50%',
        background: `radial-gradient(circle, ${c.glow} 0%, rgba(0,0,0,0) 70%)`,
        filter: 'blur(4px)',
      }} />
      {/* calligraphic initial */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: '"Amiri", "Scheherazade New", serif',
        fontWeight: 700,
        fontSize: size * 0.95,
        color: c.ink,
        textShadow: `0 0 16px ${c.glow}, 0 2px 0 rgba(0,0,0,0.4)`,
        marginTop: size * 0.08,
        lineHeight: 1,
      }}>
        {glyph}
      </div>
      {/* apex finial */}
      <div style={{
        position: 'absolute', top: 3, left: '50%', transform: 'translateX(-50%)',
        width: 5, height: 5, borderRadius: '50%',
        background: `radial-gradient(circle, ${c.finial[0]} 0%, ${c.finial[1]} 60%, ${c.finial[2]} 100%)`,
        boxShadow: `0 0 10px ${c.finialGlow}`,
      }} />
      {/* floor line */}
      <div style={{
        position: 'absolute', bottom: 0, left: 8, right: 8, height: 1,
        background: `linear-gradient(90deg, transparent, ${c.rim}, transparent)`,
      }} />
    </div>
  );
}
